import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { ToggleButton, ToggleButtonGroup, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import SessionCreateDialogAppBar from 'components/session/dialog/create/SessionCreateDialogAppBar';
import SessionCreateDialogContent from 'components/session/dialog/create/SessionCreateDialogContent';
import GroupSessionCreateDialogContent from 'components/session/dialog/create/GroupSessionCreateDialogContent';
import SlideUpTransition from 'components/shared/transitions/SlideUpTransition';
import StyledSessionDialogCreate from 'common/theme/dialog/create-session/StyledSessionDialogCreate';
import { isAdmin } from 'common/utils/user';
import clientSlice from 'store/modules/client';
import sessionTypeSlice from 'store/modules/sessionType';
import trainerSlice from 'store/modules/trainer';

function SessionDialogCreate({ onClose, open }) {
  const dispatch = useDispatch();
  const { t } = useTranslation(['labels', 'sessions']);
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
  const [isGroupSession, setIsGroupSession] = useState(false);

  const role = useSelector((state) => state.auth.user.role);
  const isAdminUser = isAdmin(role);

  useEffect(() => {
    dispatch(sessionTypeSlice.actions.listAll());
  }, [dispatch]);

  const onDialogClose = useCallback(() => {
    if (isAdminUser) {
      dispatch(clientSlice.actions.clearSearchResults());
    }

    dispatch(trainerSlice.actions.clearList());
    dispatch(trainerSlice.actions.clearAvailableTimeSlots());
    setIsGroupSession(false);
    onClose();
  }, [dispatch, isAdminUser, onClose]);

  return (
    <StyledSessionDialogCreate
      fullWidth
      fullScreen={fullScreen}
      maxWidth="lg"
      open={open}
      onClose={onDialogClose}
      TransitionComponent={SlideUpTransition}>
      <SessionCreateDialogAppBar onDialogClose={onDialogClose}>
        {isAdminUser && (
          <ToggleButtonGroup
            value={isGroupSession ? 'group' : 'individual'}
            exclusive
            size="small"
            onChange={(_, v) => v && setIsGroupSession(v === 'group')}
            sx={{
              ml: 1,
              '& .MuiToggleButton-root': {
                color: 'text.secondary',
                borderColor: 'hairline.strong',
                textTransform: 'none',
                fontWeight: 700,
                px: 1.75,
                py: 0.4
              },
              '& .Mui-selected': {
                color: (th) => `${th.palette.gold.main} !important`,
                backgroundColor: (th) => `${th.palette.gold.soft} !important`,
                borderColor: (th) => `${th.palette.gold.main} !important`
              }
            }}>
            <ToggleButton value="individual">{t('individual', { ns: 'labels' })}</ToggleButton>
            <ToggleButton value="group">{t('group', { ns: 'labels' })}</ToggleButton>
          </ToggleButtonGroup>
        )}
      </SessionCreateDialogAppBar>
      {isGroupSession ? (
        <GroupSessionCreateDialogContent onDialogClose={onDialogClose} />
      ) : (
        <SessionCreateDialogContent onDialogClose={onDialogClose} />
      )}
    </StyledSessionDialogCreate>
  );
}

SessionDialogCreate.propTypes = {
  onClose: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired
};

export default SessionDialogCreate;
